/**
 * Camera v0.2
 *
 * 「カメラビュー」を 4:5 のレンズ枠として中央配置するコンポーネント。
 *
 * - 親(App.tsx)から渡された videoRef に MediaStream を流し込む
 * - 緑ティント、コーナーブラケット、中央十字線、4隅のHUDメタを重ねる
 * - SCAN中のスキャンラインは ScanLine コンポーネントを別途重ねるため、ここでは扱わない
 *
 * iOS Safari の playsInline / muted / autoPlay は v0.1 から継承。
 */

import { useEffect } from 'react';
import type { MutableRefObject } from 'react';

import { useCamera } from '../hooks/useCamera';

import styles from './Camera.module.css';

export interface CameraProps {
  /** App側で作って渡す<video>要素のref */
  videoRef: MutableRefObject<HTMLVideoElement | null>;
}

export function Camera({ videoRef }: CameraProps) {
  const { stream, status, error } = useCamera();

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream ?? null;
  }, [stream, videoRef]);

  return (
    <div className={styles.root}>
      <video
        ref={videoRef}
        className={styles.video}
        autoPlay
        playsInline
        muted
      />

      {/* 緑ティント（カメラの上に薄く重ねる） */}
      <div className={styles.tint} aria-hidden />

      {/* 中央十字線 */}
      <div className={styles.crosshair} aria-hidden>
        <span className={styles.crosshairH} />
        <span className={styles.crosshairV} />
      </div>

      {/* 4隅のコーナーブラケット */}
      <span className={`${styles.bracket} ${styles.bracketTopLeft}`} aria-hidden />
      <span className={`${styles.bracket} ${styles.bracketTopRight}`} aria-hidden />
      <span className={`${styles.bracket} ${styles.bracketBottomLeft}`} aria-hidden />
      <span className={`${styles.bracket} ${styles.bracketBottomRight}`} aria-hidden />

      {/* HUD メタ表示（要件: REC / LOCK / 4.2 / FOCUS） */}
      <span className={`${styles.meta} ${styles.metaTopLeft}`} aria-hidden>REC</span>
      <span className={`${styles.meta} ${styles.metaTopRight}`} aria-hidden>⌖ LOCK</span>
      <span className={`${styles.meta} ${styles.metaBottomLeft}`} aria-hidden>▼ 4.2</span>
      <span className={`${styles.meta} ${styles.metaBottomRight}`} aria-hidden>FOCUS</span>

      {/* 状態オーバーレイ */}
      {status === 'requesting' && (
        <div className={styles.overlay} role="status">
          <p className={styles.overlayTitle}>INITIALIZING</p>
          <p className={styles.overlayMessage}>
            カメラを起動しています…
            <br />
            ブラウザの確認ダイアログで「許可」を選んでください。
          </p>
        </div>
      )}

      {status === 'error' && (
        <div className={styles.overlay} role="alert">
          <p className={styles.overlayTitle}>CAMERA ERROR</p>
          <p className={`${styles.overlayMessage} ${styles.overlayMessageError}`}>
            {error ?? 'カメラを起動できませんでした。'}
          </p>
        </div>
      )}
    </div>
  );
}
